/**
 * Configuration → Business Opportunities → Matrix — admins only. The Impact ×
 * Feasibility chart (Business Opportunities' Matrix view and the Dashboard):
 * which dimensions feed each axis (set per dimension on Dimensions, shown
 * here), and the priority axes: where the chart draws its dashed lines.
 * Presentation only: research never sees it, so changing it moves no score,
 * total, ranking or verdict, and makes no prompt version.
 */

import { useEffect, useState } from 'react';

import { MatrixIn, matrixInThresholdMax } from '@/api/generated/schemas';
import { useIdeaCatalogue, useMatrix, useSaveMatrix } from '@/api/queries';
import type { Axis, IdeaCatalogue } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { FormError, LoadFailure, LoadingCard } from '@/ui/StateCard';
import { buttonState } from '@/ui/ActionButton';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { FieldLabel } from '@/ui/FieldLabel';
import { SaveBar } from '@/ui/SaveBar';
import { useSession } from '@/session/SessionContext';

// The generator names no constant for a minimum of 1; the schema still carries it.
const THRESHOLD_MIN = MatrixIn.shape.threshold.minValue ?? 1;
const THRESHOLD_MAX = matrixInThresholdMax;

const AXES: { axis: Axis | null; name: string; note: string }[] = [
  { axis: 'impact', name: 'Impact', note: 'Horizontal axis' },
  { axis: 'feasibility', name: 'Feasibility', note: 'Vertical axis' },
  { axis: null, name: 'Neither', note: 'In the total only' },
];

type CatalogueDimension = IdeaCatalogue['dimensions'][number];

function isThreshold(value: number | ''): value is number {
  return value !== '' && Object.keys(fieldErrors(MatrixIn, { threshold: value })).length === 0;
}

function AxesCard({ dimensions }: { dimensions: CatalogueDimension[] }) {
  return (
    <div className="card">
      <div className="card-body stack-md">
        <h3 className="config-subhead">Axes</h3>
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th scope="col">Axis</th>
                <th scope="col">Dimensions, each by its weight</th>
              </tr>
            </thead>
            <tbody>
              {AXES.map(({ axis, name, note }) => {
                const feeding = dimensions.filter((d) => (d.axis ?? null) === axis);
                return (
                  <tr key={name}>
                    <td>
                      <b>{name}</b>
                      <span className="sub">{note}</span>
                    </td>
                    <td>{feeding.length ? feeding.map((d) => `${d.label} (${d.weight}%)`).join(', ') : <span className="hint">None</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface ThresholdProps {
  stored: number | null;
  threshold: number | '';
  setThreshold: (value: number | '') => void;
  editable: boolean;
  save: ReturnType<typeof useSaveMatrix>;
}

function ThresholdCard(props: ThresholdProps) {
  const { stored, threshold, setThreshold, editable, save } = props;
  const valid = isThreshold(threshold);
  const error = valid ? serverFieldErrors(save.error).threshold : `A whole number from ${THRESHOLD_MIN} to ${THRESHOLD_MAX}.`;
  return (
    <div className="card">
      <form
        className="card-body stack-md"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid && threshold !== stored) save.mutate({ threshold });
        }}
      >
        <h3 className="config-subhead">Priority axes</h3>
        <div className="field matrix-split">
          <FieldLabel
            htmlFor="matrix-split"
            id="matrix-split-hint"
            label="Drawn at"
            hint="Where the matrix draws its dashed lines. Ideas above both are Priorities."
          />
          <div className="input-suffix">
            <input
              id="matrix-split"
              type="number"
              inputMode="numeric"
              step={1}
              min={THRESHOLD_MIN}
              max={THRESHOLD_MAX}
              {...fieldA11y('matrix-split', error, 'matrix-split-hint')}
              value={threshold}
              readOnly={!editable}
              onChange={(e) => setThreshold(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <span className="suffix" aria-hidden="true">pts</span>
          </div>
          <FieldError inputId="matrix-split" message={error} />
        </div>
        <FormError error={bannerError(save.error, ['threshold'])} />
        {editable ? (
          <SaveBar changed={threshold !== stored && !save.isPending} valid={valid} state={buttonState(save)} onReset={() => setThreshold(stored ?? '')} />
        ) : (
          <p className="hint">You can view the matrix split but not change it.</p>
        )}
      </form>
    </div>
  );
}

export function MatrixSection() {
  const matrix = useMatrix();
  const catalogue = useIdeaCatalogue();
  const save = useSaveMatrix();
  const editable = useSession().can('research.matrix.manage');
  const stored = matrix.data?.threshold ?? null;
  const [threshold, setThreshold] = useState<number | ''>('');
  useEffect(() => {
    if (stored !== null) setThreshold(stored);
  }, [stored]);

  if (matrix.isPending || catalogue.isPending) return <LoadingCard label="Loading the matrix" />;
  const failure = matrix.error ?? catalogue.error;
  if (failure) {
    return (
      <LoadFailure
        error={failure}
        onRetry={() => {
          void matrix.refetch();
          void catalogue.refetch();
        }}
      />
    );
  }

  return (
    <>
      <AxesCard dimensions={catalogue.data?.dimensions ?? []} />
      <ThresholdCard stored={stored} threshold={threshold} setThreshold={setThreshold} editable={editable} save={save} />
    </>
  );
}
